import { useEffect, useState, type FormEvent } from 'react';
import { formatDate } from '../../util/datetime';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

import * as adminApi from '../../api/admin';
import type { PlatformAdmin } from '../../api/admin';
import { useAuth } from '../../auth/AuthContext';
import { validateEmail } from '../../validation/profile';
import { RefreshButton } from '../../components/RefreshButton';
import { PendingInvitations } from '../../components/PendingInvitations';
import { SortHeader } from '../../components/SortHeader';
import { useTableSort, sortRows } from '../../util/tableSort';

type AdminSortKey =
  | 'email'
  | 'full_name'
  | 'email_verified_at'
  | 'is_active'
  | 'created_at';

/**
 * Platform administrators (superadmin only).
 *
 * Lists every user who currently holds a superadmin membership row
 * (tenant_id NULL) and exposes the magic-link invitation flow to add
 * more. The invitee follows the same /accept-invite path as a tenant
 * invitation; the only behavioural difference is that the resulting
 * membership has tenant_id=NULL and role=superadmin.
 *
 * No revoke / demote UI on this page — that needs a stronger confirm
 * because removing the last superadmin would lock everyone out. We'll
 * land it behind an ops-grade modal once the platform has more than a
 * couple of operators.
 */
export function PlatformAdmins({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<PlatformAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track which row's Switch is mid-toggle so we don't double-fire.
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Click-to-sort headers, matching the other admin list tables.
  const sort = useTableSort<AdminSortKey>('email');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSentTo, setInviteSentTo] = useState<string | null>(null);
  // Bumped after an invite send so the pending-invitations panel
  // (a separate fetch from the roster) refetches and shows the new row.
  const [pendingReloadKey, setPendingReloadKey] = useState(0);

  const [refreshing, setRefreshing] = useState(false);

  const reload = async () => {
    setError(null);
    setRefreshing(true);
    try {
      const rows = await adminApi.listPlatformAdmins();
      setAdmins(rows);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to load platform admins',
      );
      setAdmins([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const toggleActive = async (a: PlatformAdmin) => {
    setTogglingId(a.id);
    setError(null);
    // Optimistic flip so the Switch feels responsive. Reverts in the
    // catch if the server rejects (e.g. 403 self-toggle).
    setAdmins((prev) =>
      prev
        ? prev.map((row) =>
            row.id === a.id ? { ...row, is_active: !row.is_active } : row,
          )
        : prev,
    );
    try {
      await adminApi.updatePlatformAdmin(a.id, { is_active: !a.is_active });
    } catch (err: unknown) {
      setAdmins((prev) =>
        prev
          ? prev.map((row) =>
              row.id === a.id ? { ...row, is_active: a.is_active } : row,
            )
          : prev,
      );
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to toggle platform admin',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const emailError = validateEmail(inviteEmail);
  const inviteDisabled =
    inviteSubmitting ||
    inviteEmail.trim() === '' ||
    emailError !== null;

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSentTo(null);
    setInviteSubmitting(true);
    try {
      await adminApi.invitePlatformAdmin(inviteEmail.trim());
      setInviteSentTo(inviteEmail.trim());
      setPendingReloadKey((k) => k + 1);
      setInviteEmail('');
      // Show the success state inside the dialog briefly, then close
      // and reload the list. The list itself only changes when the
      // invitee accepts.
      setTimeout(() => {
        setInviteOpen(false);
        setInviteSentTo(null);
      }, 1500);
    } catch (err: unknown) {
      setInviteError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to send invitation',
      );
    } finally {
      setInviteSubmitting(false);
    }
  };

  const actions = (
    <Stack direction="row" spacing={1}>
      <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => {
          setInviteEmail('');
          setInviteError(null);
          setInviteSentTo(null);
          setInviteOpen(true);
        }}
      >
        Invite admin
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={3}>
      {actionsSlot ? (
        createPortal(actions, actionsSlot)
      ) : (
        <Stack
          direction="row"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="center"
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Platform Admins</Typography>
              <Typography variant="body2" color="text.secondary">
                10NEWTONS operators with superadmin membership (tenant-less).
              </Typography>
            </Stack>
          )}
          {actions}
        </Stack>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {admins === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          {admins.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No platform admins yet. Use the CLI to bootstrap the first
                one, then invite the rest from here.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
            <TableHead>
              <TableRow>
                <SortHeader label="Email" col="email" sort={sort} />
                <SortHeader label="Name" col="full_name" sort={sort} />
                <SortHeader
                  label="Verified"
                  col="email_verified_at"
                  sort={sort}
                />
                <SortHeader label="Enabled" col="is_active" sort={sort} />
                <SortHeader
                  label="Member since"
                  col="created_at"
                  sort={sort}
                />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortRows(
                admins,
                (a) => {
                  switch (sort.orderBy) {
                    case 'email':
                      return a.email;
                    case 'full_name':
                      return a.full_name;
                    case 'email_verified_at':
                      return a.email_verified_at;
                    case 'is_active':
                      return a.is_active;
                    case 'created_at':
                      return a.created_at;
                  }
                },
                sort.order,
              ).map((a) => {
                const isSelf = a.id === currentUser?.id;
                // Route choice: clicking your own row drops you into
                // the editable Profile page. Clicking anyone else's
                // opens the read-only ViewPlatformAdmin view.
                const target = isSelf
                  ? '/profile'
                  : `/admin/platform-admins/${a.id}`;
                const rowTooltip = isSelf
                  ? 'Open your editable profile'
                  : `Open ${a.full_name || a.email}'s profile (read-only)`;
                // Pencil icon mirrors the affordance on the Tenants page.
                // For your own row it lands on the editable Profile; for
                // anyone else it lands on the read-only view — same
                // destination as a row-click, just an explicit handle.
                const editTooltip = isSelf
                  ? 'Edit your profile'
                  : 'View profile (read-only)';
                const switchTooltip = isSelf
                  ? "You can't disable your own admin access — ask another platform admin."
                  : a.is_active
                  ? 'Suspend this platform admin (their superadmin grant becomes inactive)'
                  : 'Re-enable this platform admin';
                return (
                  <Tooltip title={rowTooltip} key={a.id} placement="left">
                    <TableRow
                      hover
                      onClick={() => navigate(target)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {a.email}
                        {isSelf && (
                          <Chip
                            label="you"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{a.full_name ?? '—'}</TableCell>
                      <TableCell>
                        {a.email_verified_at ? (
                          <Chip label="Verified" size="small" color="success" />
                        ) : (
                          <Chip label="Pending" size="small" color="warning" />
                        )}
                      </TableCell>
                      {/* Enabled — the active/suspended state + its toggle in
                          one column (was a redundant Status chip + Active
                          switch). Matches the tenant-admin table. */}
                      <TableCell
                        // Stop the click from bubbling to the row's
                        // navigate-on-click handler — the chip/switch here
                        // shouldn't open the detail page.
                        onClick={(e) => e.stopPropagation()}
                        sx={{ cursor: 'default' }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {a.is_active ? (
                            <Chip label="Active" size="small" color="success" />
                          ) : (
                            <Chip
                              label="Suspended"
                              size="small"
                              color="warning"
                            />
                          )}
                          <Tooltip title={switchTooltip}>
                            {/* span needed because a disabled Switch eats
                                pointer events and Tooltip needs a hover
                                target. */}
                            <span>
                              <Switch
                                size="small"
                                checked={a.is_active}
                                disabled={isSelf || togglingId === a.id}
                                onChange={() => void toggleActive(a)}
                                inputProps={{
                                  'aria-label': `Toggle active for ${a.email}`,
                                }}
                              />
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {formatDate(a.created_at)}
                      </TableCell>
                      <TableCell
                        align="right"
                        // Stop the click from bubbling to the row's
                        // navigate-on-click handler so the icon owns
                        // its own click target (a future per-action
                        // here, e.g. confirm-revoke, would expect to
                        // run on its own without also re-firing the
                        // row navigate).
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title={editTooltip}>
                          <span>
                            <IconButton
                              onClick={() => navigate(target)}
                              aria-label={
                                isSelf
                                  ? 'Edit your profile'
                                  : `View ${a.email}'s profile`
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </Tooltip>
                );
              })}
            </TableBody>
          </Table>
            </TableContainer>
          )}

          {/* Invited-but-not-joined platform admins. Renders nothing when
              there are none outstanding — otherwise a divider + this panel
              fold into the same card as the roster above. */}
          <PendingInvitations
            embedded
            source={{
              list: adminApi.listPlatformInvitations,
              resend: (inv) =>
                adminApi.invitePlatformAdmin(inv.email).then(() => undefined),
              cancel: (inv) => adminApi.revokePlatformInvitation(inv.id),
            }}
            canWrite
            reloadKey={pendingReloadKey}
          />
        </Card>
      )}

      <Dialog
        open={inviteOpen}
        onClose={() => (inviteSubmitting ? null : setInviteOpen(false))}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleInvite}>
          <DialogTitle>Invite a platform admin</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The recipient will receive a magic-link email asking them to
              accept the platform-admin role. If they don't already have a
              10NEWTONS account, the link lets them set a password in one
              click — no separate signup step.
            </DialogContentText>
            <TextField
              autoFocus
              label="Email"
              type="email"
              fullWidth
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              error={inviteEmail.trim() !== '' && emailError !== null}
              helperText={
                inviteEmail.trim() !== '' && emailError
                  ? emailError
                  : 'They\'ll be granted a superadmin membership with tenant_id NULL.'
              }
              disabled={inviteSubmitting}
            />
            {inviteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {inviteError}
              </Alert>
            )}
            {inviteSentTo && !inviteError && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Invitation sent to <strong>{inviteSentTo}</strong>. The
                link expires in 7 days.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setInviteOpen(false)}
              disabled={inviteSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={inviteDisabled}
            >
              {inviteSubmitting ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Stack>
  );
}
